import { LetterContent } from '@/components/editor/letter-editor';
import { type Letter, getSignatureFontFamily, letters } from '@/lib/api';
import { useDateLocale } from '@/lib/hooks/use-date-locale';
import { routes } from '@/lib/routes';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Clock, Loader2, MapPin, Plane, Reply } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router';

export default function LetterViewPage() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const params = useParams();
  const navigate = useNavigate();
  const letterId = params.id as string;

  const [letter, setLetter] = useState<Letter | null>(null);
  const [thread, setThread] = useState<Letter[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchLetter = async () => {
      try {
        const [letterData, threadData] = await Promise.all([
          letters.getById(letterId),
          letters.getThread(letterId)
        ]);
        setLetter(letterData);
        setThread(threadData ?? []);
      } catch {
        setNotFound(true);
        setThread([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLetter();
  }, [letterId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-sepia animate-spin" />
      </div>
    );
  }

  if (notFound || !letter) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-medium text-ink mb-2">{t('common.error')}</h2>
        <p className="text-sepia/70 mb-6">{t('common.error')}</p>
        <Link
          to={routes.inbox}
          className="inline-flex items-center gap-2 text-blue hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Link>
      </div>
    );
  }

  const otherUser = letter.sender || letter.recipient;
  const isReceived = !!letter.sender;
  // For received letters, use the captured display name from send time
  const otherUserDisplayName = isReceived
    ? letter.senderDisplayName || otherUser?.username
    : otherUser?.displayName || otherUser?.username;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-warm/30 transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-sepia" />
          </button>
          <div>
            <h1 className="text-2xl font-serif text-ink">{letter.subject}</h1>
            <p className="text-sepia/70 text-sm">
              {isReceived ? t('letter.from') : t('letter.to')}: {otherUserDisplayName || 'Unknown'}
              {otherUser?.username && otherUserDisplayName !== otherUser.username && (
                <span className="text-sepia/50"> (@{otherUser.username})</span>
              )}
            </p>
          </div>
        </div>

        {/* Reply button - only for received letters */}
        {isReceived && letter.status !== 'DRAFT' && (
          <Link
            to={routes.replyToLetter(letter.senderId, letter.id)}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg font-medium hover:bg-gold/90 transition-all"
          >
            <Reply className="w-4 h-4" />
            {t('letter.reply')}
          </Link>
        )}
      </div>

      {/* Thread (if exists) */}
      {thread.length > 1 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-sepia mb-3">{t('letter.thread')}</h3>
          <div className="space-y-2">
            {thread.map((threadLetter) => (
              <Link
                key={threadLetter.id}
                to={routes.letter(threadLetter.id)}
                className={`block p-3 rounded-lg transition-all ${
                  threadLetter.id === letter.id
                    ? 'bg-blue/10 border-l-4 border-blue'
                    : 'bg-gray-warm/20 hover:bg-gray-warm/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-medium text-ink text-sm">{threadLetter.subject}</p>
                  <span className="text-xs text-sepia/60">
                    {threadLetter.sentAt
                      ? format(new Date(threadLetter.sentAt), 'MMM d, yyyy')
                      : t('letter.status.draft')}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Letter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg overflow-hidden"
        style={{ boxShadow: 'var(--shadow-md)' }}
      >
        {/* Letter header */}
        <div className="p-6 border-b border-gray-warm/30">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-gray-warm/50 flex items-center justify-center text-xl font-medium text-sepia">
              {(otherUserDisplayName || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium text-ink">{otherUserDisplayName || 'Unknown'}</p>
                {otherUser?.username && otherUserDisplayName !== otherUser.username && (
                  <span className="text-sepia/60 text-sm">@{otherUser.username}</span>
                )}
                {otherUser?.country && (
                  <span className="flex items-center gap-1 text-xs text-sepia/60">
                    <MapPin className="w-3 h-3" />
                    {otherUser.country}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-sepia/60">
                {letter.sentAt && (
                  <span>
                    {t('letter.sentOn', {
                      date: format(new Date(letter.sentAt), "MMM d, yyyy 'at' h:mm a")
                    })}
                  </span>
                )}
                {letter.deliveredAt && letter.status === 'IN_TRANSIT' && (
                  <span className="flex items-center gap-1 text-blue">
                    <Plane className="w-4 h-4" />
                    {t('letter.arrivedOn', {
                      date: format(new Date(letter.deliveredAt), 'MMM d, yyyy')
                    })}
                  </span>
                )}
              </div>
            </div>

            {/* Status badge */}
            <StatusBadge status={letter.status} />
          </div>
        </div>

        {/* Letter content */}
        <div className="p-6">
          <LetterContent
            content={letter.content}
            fontFamily={
              letter.senderLetterFont ? getSignatureFontFamily(letter.senderLetterFont) : undefined
            }
          />

          {/* Sender's Signature */}
          {letter.senderSignatureText && (
            <div className="mt-8 pt-4 border-t border-gray-warm/30">
              <p
                className="text-xl text-blue text-right"
                style={{
                  fontFamily: getSignatureFontFamily(letter.senderSignatureFont || 'CAVEAT')
                }}
              >
                {letter.senderSignatureText}
              </p>
            </div>
          )}
        </div>

        {/* Footer with timestamps */}
        <div className="px-6 py-4 bg-cream/50 border-t border-gray-warm/30">
          <div className="flex items-center gap-6 text-xs text-sepia/50">
            {letter.sentAt && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {t('letter.sentOn', {
                  date: format(new Date(letter.sentAt), 'PPpp', { locale: dateLocale })
                })}
              </span>
            )}
            {letter.deliveredAt && letter.status !== 'IN_TRANSIT' && (
              <span className="flex items-center gap-1">
                <Check className="w-3.5 h-3.5" />
                {t('letter.deliveredOn', {
                  date: format(new Date(letter.deliveredAt), 'PPpp', { locale: dateLocale })
                })}
              </span>
            )}
            {letter.readAt && (
              <span className="flex items-center gap-1">
                <Check className="w-3.5 h-3.5" />
                <Check className="w-3.5 h-3.5 -ml-2" />
                {t('letter.readOn', {
                  date: format(new Date(letter.readAt), 'PPpp', { locale: dateLocale })
                })}
              </span>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function StatusBadge({ status }: { status: Letter['status'] }) {
  const { t } = useTranslation();

  const config = {
    DRAFT: { label: t('letter.status.draft'), className: 'bg-gray-warm/30 text-sepia' },
    IN_TRANSIT: { label: t('letter.status.inTransit'), className: 'bg-blue/10 text-blue' },
    DELIVERED: { label: t('letter.status.delivered'), className: 'bg-sage/20 text-sage' },
    READ: { label: t('letter.status.read'), className: 'bg-sage/20 text-sage' }
  };

  const { label, className } = config[status];

  return <span className={`px-3 py-1 rounded-full text-xs font-medium ${className}`}>{label}</span>;
}
